import { Action, InitState, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { ProjectsService } from './project.service';
import {
    BillingMilestoneModel,
    BusinessActivityModel,
    PhaseModel,
    ProjectModel,
    ProjectStatus,
    ResearchCategory,
    SubcontractorInvoiceModel,
} from './project.models';
import { ProjectsPageSize } from 'src/app/shared/all.constants';
import { User } from 'src/app/core/models/user.model';

/** This file is divided in 3 parts:
 * - The definition of the Store : defining data structure
 * - Constants that allow NgXs to trigger Actions
 * - Selectors & Actions : Functions allowed to interact with the store
 *
 */

export interface Pagination {
    q?: string | null; //Code ou label (recherche libre)
    activityId?: string[] | null;
    status?: ProjectStatus[] | null;
    leadUser?: string[] | null;
    offset?: number | null;
    limit?: number | null;
    sort?: { key: string; direction: number };
    reset?: boolean;
}

// Store definition
export interface ProjectsStateModel {
    allProjects: ProjectModel[] | null;
    lastQueryProjects: ProjectModel[] | null;
    projectWithDetail: ProjectModel | null;
    pagination: Pagination;
    activities: BusinessActivityModel[];
    researchCategories: ResearchCategory[];
    leadUsers: User[];
    totalCount: number;
    isLoading: boolean;
    lastSearch: Date;
}

//Constants

export class InitProjectState {
    static readonly type = 'PROJECTS/INIT';
    constructor(public payload: {}) {}
}

export class GetProjects {
    static readonly type = 'PROJECTS/ALL';
    constructor(public payload: { pagination: Pagination }) {}
}
export class GetProjectByCode {
    static readonly type = 'PROJECTS/BYCODE';
    constructor(public payload: { code: string }) {}
}
export class GetProjectById {
    static readonly type = 'PROJECTS/BYPROJECTID';
    constructor(public payload: { projectId: string }) {}
}
export class GetProjectByPhaseId {
    static readonly type = 'PROJECTS/BYPHASEID';
    constructor(public payload: { phaseId: string }) {}
}

export class UpdateProject {
    static readonly type = 'PROJECTS/UPDATE';
    constructor(public payload: { project: Partial<ProjectModel> }) {}
}

export class CreateProject {
    static readonly type = 'PROJECTS/CREATE';
    constructor(public payload: { project: Partial<ProjectModel> }) {}
}

export class UpdatePhase {
    static readonly type = 'PROJECTS/PHASE/UPDATE';
    constructor(public payload: { phase: Partial<PhaseModel>; projectId: string }) {}
}

export class CreatePhase {
    static readonly type = 'PROJECTS/PHASE/CREATE';
    constructor(public payload: { phase: Partial<PhaseModel>; projectId: string }) {}
}

export class UpdateBillingMilestone {
    static readonly type = 'PROJECTS/BILLINGMILESTONE/UPDATE';
    constructor(
        public payload: {
            billingMilestone: Partial<BillingMilestoneModel>;
            projectId: string;
            updateSoldAmount: boolean;
        },
    ) {}
}
export class AddBillingMilestones {
    static readonly type = 'PROJECTS/BILLINGMILESTONE/ADD';
    constructor(
        public payload: {
            billingMilestones: Partial<BillingMilestoneModel>[];
            projectId: string;
            updateSoldAmount: boolean;
        },
    ) {}
}
export class RemoveBillingMilestone {
    static readonly type = 'PROJECTS/BILLINGMILESTONE/REMOVE';
    constructor(
        public payload: {
            billingMilestoneId: string;
            projectId: string;
            updateSoldAmount: boolean;
        },
    ) {}
}

export class UpdateSubcontractorInvoice {
    static readonly type = 'PROJECTS/SUBCONTRACTOR/UPDATE';
    constructor(
        public payload: {
            subcontractorInvoice: Partial<SubcontractorInvoiceModel>;
            projectId: string;
            updateSubcontractingAmount: boolean;
        },
    ) {}
}
export class AddSubcontractorInvoices {
    static readonly type = 'PROJECTS/SUBCONTRACTOR/ADD';
    constructor(
        public payload: {
            subcontractorInvoices: Partial<SubcontractorInvoiceModel>[];
            projectId: string;
            updateSubcontractingAmount: boolean;
        },
    ) {}
}
export class RemoveSubcontractorInvoice {
    static readonly type = 'PROJECTS/SUBCONTRACTOR/REMOVE';
    constructor(
        public payload: {
            subcontractorInvoiceId: string;
            projectId: string;
            updateSubcontractingAmount: boolean;
        },
    ) {}
}

//Actions

@State<ProjectsStateModel>({
    name: 'Projects',
    defaults: {
        allProjects: [],
        lastQueryProjects: [],
        projectWithDetail: null,
        activities: [],
        researchCategories: [],
        leadUsers: [],
        pagination: {
            q: null, //Code ou label (recherche libre)
            activityId: undefined,
            status: undefined,
            offset: 0,
            limit: 100,
            sort: { key: 'code', direction: -1 },
        },
        totalCount: 0,
        isLoading: false,
        lastSearch: new Date(),
    },
})
@Injectable()
export class ProjectsState {
    @Selector()
    public static lastQueryProjects(state: ProjectsStateModel): ProjectModel[] | null {
        return state.lastQueryProjects;
    }
    @Selector()
    public static allProjects(state: ProjectsStateModel): ProjectModel[] | null {
        return state.allProjects;
    }

    @Selector()
    public static projectWithDetail(state: ProjectsStateModel): ProjectModel | null {
        return state.projectWithDetail;
    }

    @Selector()
    public static activities(state: ProjectsStateModel): BusinessActivityModel[] | null {
        return state.activities;
    }
    @Selector()
    public static researchCategories(state: ProjectsStateModel): ResearchCategory[] | null {
        return state.researchCategories;
    }
    @Selector()
    public static leadUsers(state: ProjectsStateModel): User[] | null {
        return state.leadUsers;
    }

    @Selector()
    public static totalCount(state: ProjectsStateModel): number {
        return state.totalCount;
    }

    @Selector()
    public static pagination(state: ProjectsStateModel): Pagination {
        return state.pagination;
    }
    @Selector()
    public static isLoading(state: ProjectsStateModel): boolean {
        return state.isLoading;
    }

    constructor(
        private projectsService: ProjectsService,
        private readonly store: Store,
    ) {}

    @Action(InitProjectState)
    public async initProjectState(
        ctx: StateContext<ProjectsStateModel>,
        action: InitProjectState,
    ): Promise<void> {
        const activities = await this.projectsService.getActivities();
        const researchCategories = await this.projectsService.getResearchCategories();
        const leadUsers = await this.projectsService.getLeadUsers();
        ctx.patchState({ leadUsers, activities, researchCategories });
    }

    @Action(GetProjects)
    public async getProjects(
        ctx: StateContext<ProjectsStateModel>,
        action: GetProjects,
    ): Promise<void> {
        const currentSearch = new Date();
        ctx.patchState({ isLoading: true, lastSearch: currentSearch });
        const pagination = action.payload.pagination;
        pagination.offset = pagination.offset || 0;
        pagination.limit = pagination.limit || ProjectsPageSize;
        const { total, items } = await this.projectsService.getProjects(pagination);
        const currentState = ctx.getState();
        if (currentState.lastSearch.getTime() !== currentSearch.getTime()) return;
        let allProjects = [...(pagination.reset ? [] : currentState.allProjects || [])];
        items.map((item) => {
            if (!currentState.allProjects?.find((p) => p.id === item.id)) {
                allProjects.push({
                    ...item,
                    estimatedCloseDate: item.estimatedCloseDate
                        ? new Date(item.estimatedCloseDate)
                        : null,
                });
            }
        });

        //We only change totalCount if no filters
        const totalCount =
            pagination.q || pagination.activityId || pagination.activityId || pagination.leadUser
                ? currentState.totalCount
                : total;
        pagination.reset = false;
        ctx.patchState({
            lastQueryProjects: items || [],
            allProjects,
            totalCount,
            pagination,
            isLoading: false,
        });
    }

    @Action(GetProjectById)
    public async getProjectById(
        ctx: StateContext<ProjectsStateModel>,
        action: GetProjectById,
    ): Promise<void> {
        ctx.patchState({ projectWithDetail: undefined });
        const project = await this.projectsService.getProjectById(action.payload.projectId);
        this.addProjectToState(ctx, project);
    }

    @Action(GetProjectByPhaseId)
    public async getProjectByPhaseId(
        ctx: StateContext<ProjectsStateModel>,
        action: GetProjectByPhaseId,
    ): Promise<void> {
        ctx.patchState({ projectWithDetail: undefined });
        const project = await this.projectsService.getProjectByPhaseId(action.payload.phaseId);
        this.addProjectToState(ctx, project);
    }

    @Action(GetProjectByCode)
    public async getProjectByCode(
        ctx: StateContext<ProjectsStateModel>,
        action: GetProjectByCode,
    ): Promise<void> {
        ctx.patchState({ projectWithDetail: undefined });
        const project = await this.projectsService.getProjectByCode(action.payload.code);
        this.addProjectToState(ctx, project);
    }

    addProjectToState(ctx: StateContext<ProjectsStateModel>, project: ProjectModel) {
        const currentState = ctx.getState();
        let allProjects = [...(currentState.allProjects || [])];
        let alreadyInArray = false;
        allProjects = allProjects.map((p) => {
            if (p.id === project.id) {
                alreadyInArray = true;
                return project;
            }
            return p;
        });
        if (!alreadyInArray) allProjects = [...allProjects, project];
        ctx.patchState({ allProjects, projectWithDetail: project });
    }

    @Action(UpdateProject)
    public async updateProject(
        ctx: StateContext<ProjectsStateModel>,
        action: UpdateProject,
    ): Promise<void> {
        const updatedProject = await this.projectsService.updateProject(action.payload.project);
        const currentState = ctx.getState();

        const allProjects = currentState.allProjects?.map((project) =>
            project.id === updatedProject.id ? updatedProject : project,
        );
        const lastQueryProjects = currentState.lastQueryProjects?.map((project) =>
            project.id === updatedProject.id ? updatedProject : project,
        );
        ctx.patchState({ allProjects, lastQueryProjects, projectWithDetail: updatedProject });
    }

    @Action(CreateProject)
    public async createProject(
        ctx: StateContext<ProjectsStateModel>,
        action: CreateProject,
    ): Promise<ProjectModel> {
        try {
            const newProject = await this.projectsService.createProject(action.payload.project);
            const currentState = ctx.getState();
            const allProjects = [...(currentState.allProjects || []), newProject];
            const lastQueryProjects = [...(currentState.lastQueryProjects || []), newProject];
            ctx.patchState({ allProjects, lastQueryProjects });
            return newProject;
        } catch (err: any) {
            console.error('Error creating project:', err);
            throw err;
        }
    }

    @Action(UpdatePhase)
    public async updatePhase(
        ctx: StateContext<ProjectsStateModel>,
        action: UpdatePhase,
    ): Promise<void> {
        try {
            const updatedPhase: PhaseModel = await this.projectsService.updatePhase(
                action.payload.phase,
                action.payload.projectId,
            );
            const currentState = ctx.getState();
            if (currentState.projectWithDetail) {
                const detail = { ...currentState.projectWithDetail };
                detail.phases =
                    detail.phases?.map((ph) => (ph.id === updatedPhase.id ? updatedPhase : ph)) ||
                    [];
                ctx.patchState({ projectWithDetail: detail, isLoading: false });
            }
        } catch (err: any) {
            console.log(err);
        }
    }

    @Action(CreatePhase)
    public async createPhase(
        ctx: StateContext<ProjectsStateModel>,
        action: CreatePhase,
    ): Promise<void> {
        try {
            const createdPhase = await this.projectsService.createPhase(
                action.payload.phase,
                action.payload.projectId,
            );
            const currentState = ctx.getState();
            if (currentState.projectWithDetail) {
                const detail = { ...currentState.projectWithDetail };
                if (detail?.id) {
                    detail.phases = [...(detail.phases || []), createdPhase];
                }
                ctx.patchState({ projectWithDetail: detail, isLoading: false });
            }
        } catch (err: any) {
            console.log(err);
        }
    }

    @Action(UpdateBillingMilestone)
    public async updateBillingMilestone(
        ctx: StateContext<ProjectsStateModel>,
        action: UpdateBillingMilestone,
    ): Promise<void> {
        await this.projectsService.updateBillingMilestone(
            action.payload.billingMilestone,
            action.payload.projectId,
            action.payload.updateSoldAmount,
        );
        const currentState = ctx.getState();
        if (currentState.projectWithDetail?.id)
            this.store.dispatch(
                new GetProjectById({ projectId: currentState.projectWithDetail.id }),
            );
    }

    @Action(AddBillingMilestones)
    public async AddBillingMilestones(
        ctx: StateContext<ProjectsStateModel>,
        action: AddBillingMilestones,
    ): Promise<void> {
        await this.projectsService.addBillingMilestones(
            action.payload.billingMilestones,
            action.payload.projectId,
            action.payload.updateSoldAmount,
        );
        const currentState = ctx.getState();
        if (currentState.projectWithDetail?.id)
            this.store.dispatch(
                new GetProjectById({ projectId: currentState.projectWithDetail.id }),
            );
    }

    @Action(RemoveBillingMilestone)
    public async RemoveBillingMilestone(
        ctx: StateContext<ProjectsStateModel>,
        action: RemoveBillingMilestone,
    ): Promise<void> {
        await this.projectsService.deleteBillingMilestone(
            action.payload.billingMilestoneId,
            action.payload.projectId,
            action.payload.updateSoldAmount,
        );
        const currentState = ctx.getState();
        if (currentState.projectWithDetail?.id)
            this.store.dispatch(
                new GetProjectById({ projectId: currentState.projectWithDetail.id }),
            );
    }

    @Action(UpdateSubcontractorInvoice)
    public async updateSubcontractorInvoice(
        ctx: StateContext<ProjectsStateModel>,
        action: UpdateSubcontractorInvoice,
    ): Promise<void> {
        await this.projectsService.updateSubcontractorInvoice(
            action.payload.subcontractorInvoice,
            action.payload.projectId,
            action.payload.updateSubcontractingAmount,
        );
        const currentState = ctx.getState();
        if (currentState.projectWithDetail?.id)
            this.store.dispatch(
                new GetProjectById({ projectId: currentState.projectWithDetail.id }),
            );
    }

    @Action(AddSubcontractorInvoices)
    public async addSubcontractorInvoices(
        ctx: StateContext<ProjectsStateModel>,
        action: AddSubcontractorInvoices,
    ): Promise<void> {
        await this.projectsService.addSubcontractorInvoices(
            action.payload.subcontractorInvoices,
            action.payload.projectId,
            action.payload.updateSubcontractingAmount,
        );
        const currentState = ctx.getState();
        if (currentState.projectWithDetail?.id)
            this.store.dispatch(
                new GetProjectById({ projectId: currentState.projectWithDetail.id }),
            );
    }

    @Action(RemoveSubcontractorInvoice)
    public async removeSubcontractorInvoice(
        ctx: StateContext<ProjectsStateModel>,
        action: RemoveSubcontractorInvoice,
    ): Promise<void> {
        await this.projectsService.deleteSubcontractorInvoice(
            action.payload.subcontractorInvoiceId,
            action.payload.projectId,
            action.payload.updateSubcontractingAmount,
        );
        const currentState = ctx.getState();
        if (currentState.projectWithDetail?.id)
            this.store.dispatch(
                new GetProjectById({ projectId: currentState.projectWithDetail.id }),
            );
    }
}
